import { ClassNameMap } from '@material-ui/core/styles/withStyles';

import {
  Body2Text,
  CheckBox,
  DefaultCard,
  DisplayItem,
  DropDown,
  FormHookResult,
  FormInputProps,
  FormValueTypes,
  RadioButtonGroup,
  TextField,
} from 'design-system';

// Models
import { Box, Grid } from '@material-ui/core';
import clsx from 'clsx';
import validationNames from 'src/functions/validationNames';
import mapToCatalogItem from '../../../../../functions/mapToCatalogItem';
import CatalogItem from '../../../../../models/helperTypes/CatalogItem';

const dateMaskData = {
  date: true,
  delimiter: '/',
  datePattern: ['d', 'm', 'Y'],
};

const carCategoryList = mapToCatalogItem([
  { id: 1, code: 'CARGA LIVIANA', name: 'Carga Liviana' },
  { id: 2, code: 'PARTICULAR', name: 'Particular' },
  { id: 3, code: 'CARGA PESADA', name: 'Carga Pesada' },
]);

type MakeFieldProps = {
  label: string;
  key: string;
  id?: string;
  getInputProps: FormHookResult;
  classes?: ClassNameMap;
  helperText?: string;
  onChangeLicensePlate?: (val: string) => void;
  onChangeCategoryLicensePlate?: (val: CatalogItem) => void;
  onChangeNoLicensePlate?: (val: boolean) => void;
  shouldUpdate?: (newVal: string) => boolean;
  personType?: string;
};

export function makeTextField({
  label,
  key,
  getInputProps,
  id,
  helperText,
  shouldUpdate,
  personType,
}: MakeFieldProps): JSX.Element {
  return (
    <TextField
      inputId={id}
      inputLabel={label}
      inputName={getInputProps(key).name}
      defaultValue={getInputProps(key).value as string}
      inputError={getInputProps(key).error}
      inputErrorMessage={getInputProps(key).errorText}
      helperText={helperText}
      shouldUpdate={shouldUpdate}
      onChange={(val: string) => {
        const newValue = validationNames(val, key, personType);
        getInputProps(key).onChange(newValue);
      }}
    />
  );
}

export function makeTextFieldForLicense({
  label,
  key,
  getInputProps,
  id,
  helperText,
  onChangeLicensePlate,
}: MakeFieldProps): JSX.Element {
  return (
    <TextField
      inputId={id}
      inputLabel={label}
      inputName={getInputProps(key).name}
      defaultValue={getInputProps(key).value as string}
      inputError={getInputProps(key).error}
      inputErrorMessage={getInputProps(key).errorText}
      helperText={helperText}
      onChange={onChangeLicensePlate}
      isDisabled={getInputProps('noLicensePlate').value as boolean}
    />
  );
}

export function makeDateField({
  label,
  key,
  getInputProps,
  id,
}: MakeFieldProps): JSX.Element {
  return (
    <TextField
      inputId={id}
      inputLabel={label}
      inputName={getInputProps(key).name}
      defaultValue={getInputProps(key).value as string}
      placeholder="DD/MM/AAAA"
      inputError={getInputProps(key).error}
      inputErrorMessage={getInputProps(key).errorText}
      onChange={getInputProps(key).onChange}
      maskOptions={dateMaskData}
    />
  );
}

interface MakeGenderRadioProps extends MakeFieldProps {
  mappedGenders: CatalogItem[];
}
export function makeGenderRadio({
  label,
  key,
  getInputProps,
  id,
  mappedGenders,
}: MakeGenderRadioProps): JSX.Element {
  return (
    <RadioButtonGroup
      inputId={id}
      data-testid={id}
      formTitle={label}
      radioData={mappedGenders}
      radioGroupName={getInputProps(key).name}
      gridSize={6}
      defaultValue={getInputProps(key).value as string}
      errorMessage={getInputProps(key).errorText}
      onChange={(val: CatalogItem): void =>
        getInputProps(key).onChange(val.Value)
      }
    />
  );
}
interface MakeHasInvoiceFieldType extends MakeFieldProps {
  onOpenModal: () => void;
  isCollectiveProfile?: boolean;
}

export function makeHasInvoiceField({
  label,
  key,
  getInputProps,
  id,
  classes,
  isCollectiveProfile = true,
  onOpenModal,
}: MakeHasInvoiceFieldType): JSX.Element {
  if (classes !== undefined && isCollectiveProfile) {
    return (
      <Grid
        container
        className={classes.hasInvoiceContainer}
        data-testid="has-invoice"
      >
        <Grid item xs={12}>
          <Body2Text className={clsx(classes.hasInvoiceLabel)}>
            Marcar si cuenta con la factura de compra
          </Body2Text>
        </Grid>
        <Grid container alignItems="center">
          <Grid
            item
            className={clsx(classes.inputRow, classes.inputModel)}
            container
          >
            <Box>
              <CheckBox
                inputId={id}
                checkTitle={label}
                data-testid={id}
                checkName={getInputProps(key).name}
                defaultValue={getInputProps(key).value as boolean}
                onChange={getInputProps(key).onChange}
                style={{
                  alignItems: 'center',
                  fontStyle: '22px !important',
                }}
              />
            </Box>
            <Box
              display="flex"
              alignItems="center"
              data-testid="invoice-modal-mark"
              onClick={onOpenModal}
              className={classes.invoiceMark}
            >
              <img src="/svg/turquoise_questionMark.svg" alt="Question Mark" />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    );
  }
  return <></>;
}

export function makeDropDown(
  id: string,
  label: string,
  key: string,
  data: Array<CatalogItem>,
  onChange: (val: CatalogItem) => void,
  getInputProps: (
    inputName: string,
  ) => FormInputProps<FormValueTypes, FormValueTypes>,
): JSX.Element {
  return (
    <DropDown
      inputId={id}
      inputName={key}
      inputLabel={label}
      defaultValue={getInputProps(key).value as string}
      options={data}
      inputError={getInputProps(key).error}
      inputErrorMessage={getInputProps(key).errorText}
      isDisabled={key === 'typeOfMoney' ? true : data.length === 0}
      onChange={onChange}
    />
  );
}

export function makeLicensePlateComponent({
  label,
  key,
  getInputProps,
  id,
  classes,
  onChangeLicensePlate,
  onChangeCategoryLicensePlate,
  onChangeNoLicensePlate,
}: MakeFieldProps): JSX.Element {
  const valueRadio = getInputProps('noLicensePlate').value as boolean;
  const options: DisplayItem[] = [
    { Value: 'SI', Text: 'Si' },
    { Value: 'NO', Text: 'No' },
  ];
  return (
    <DefaultCard>
      <div className={classes?.formCardContent}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RadioButtonGroup
              data-testid="radio-group-category"
              inputId="sales-web-radio-filter-category"
              formTitle="¿El auto tiene placa?"
              radioData={options}
              defaultValue={valueRadio ? 'NO' : 'SI'}
              radioGroupName="radio-group-category"
              onChange={(value: DisplayItem) => {
                onChangeNoLicensePlate?.(value.Value === 'NO');
              }}
            />
          </Grid>
          {!valueRadio && (
            <>
              <Grid
                item
                xs={12}
                sm={6}
                className={clsx(classes?.inputModel)}
                data-test-id="sales-web-dropdown-categoryLicensePlate"
              >
                {makeDropDown(
                  'sales-web-dropdown-categoryLicensePlate',
                  'Categoria',
                  'carCategoryLicensePlate',
                  (getInputProps('noLicensePlate').value as boolean)
                    ? []
                    : carCategoryList,
                  onChangeCategoryLicensePlate as (val: CatalogItem) => void,
                  getInputProps,
                )}
              </Grid>
              <Grid item xs={12} sm={6} className={clsx(classes?.inputModel)}>
                {makeTextFieldForLicense({
                  getInputProps,
                  label,
                  id,
                  key,
                  helperText:
                    'Ingresa la placa sin guión o espacios (ej. ABC123)',
                  onChangeLicensePlate,
                })}
              </Grid>
            </>
          )}
        </Grid>
      </div>
    </DefaultCard>
  );
}
